import { Loader2, Pencil, RotateCcw, Save, Trash2, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { deleteTemplate, listTemplates, regenerateTemplate } from '@/api/services/nutrition'
import type { FoodTemplate, FoodTemplateItem, ItemUpdate, MealItemInput } from '@/api/types/nutrition'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { confirmDialog } from '@/lib/confirm-dialog'

import { type AddMode, FoodAddPanel } from './food-add-sections'
import { scaleQuantity } from './utils'

type TemplateEditDialogProps = {
  template: FoodTemplate | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}

type LocalItem = {
  id: number
  name: string
  quantity: string
  calories: number
  protein_g: number
  fat_g: number
  carb_g: number
  scale: number
}

function toLocalItem(item: FoodTemplateItem): LocalItem {
  return {
    id: item.id,
    name: item.name,
    quantity: item.quantity ?? '',
    calories: item.calories,
    protein_g: item.protein_g,
    fat_g: item.fat_g,
    carb_g: item.carb_g,
    scale: item.scale ?? 1,
  }
}

export function TemplateEditDialog({ template, open, onOpenChange, onSaved }: TemplateEditDialogProps) {
  const [name, setName] = useState('')
  const { isSubmitting: busy, run } = useSubmitLock()
  const [localItems, setLocalItems] = useState<LocalItem[]>([])
  const [removedIds, setRemovedIds] = useState<Set<number>>(new Set())
  const [modifiedIds, setModifiedIds] = useState<Set<number>>(new Set())
  const [editingId, setEditingId] = useState<number | null>(null)
  const [addedItems, setAddedItems] = useState<MealItemInput[]>([])
  const [openModes, setOpenModes] = useState<Set<AddMode>>(new Set())
  const [allTemplates, setAllTemplates] = useState<FoodTemplate[]>([])
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<number>>(new Set())
  const [templateMultipliers, setTemplateMultipliers] = useState<Map<number, string>>(new Map())

  const fetchTemplates = useCallback(() => {
    listTemplates().then(setAllTemplates)
  }, [])

  useEffect(() => {
    if (!open || !template) return
    setName(template.name)
    setLocalItems(template.items.map(toLocalItem))
    setRemovedIds(new Set())
    setModifiedIds(new Set())
    setEditingId(null)
    setAddedItems([])
    setOpenModes(new Set())
    setSelectedTemplateIds(new Set())
    setTemplateMultipliers(new Map())
    fetchTemplates()
  }, [open, template, fetchTemplates])

  const toggleTemplate = useCallback((id: number) => {
    setSelectedTemplateIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const templateItems = useMemo<MealItemInput[]>(() => {
    const items: MealItemInput[] = []
    for (const id of selectedTemplateIds) {
      const t = allTemplates.find((tpl) => tpl.id === id)
      if (!t) continue
      const multiplier = parseFloat(templateMultipliers.get(id) ?? '') || 1
      for (const item of t.items) {
        const itemBaseScale = item.scale ?? 1
        items.push({
          name: item.name,
          quantity: item.quantity,
          calories: item.calories * multiplier,
          protein_g: item.protein_g * multiplier,
          fat_g: item.fat_g * multiplier,
          carb_g: item.carb_g * multiplier,
          scale: itemBaseScale * multiplier,
        })
      }
    }
    return items
  }, [selectedTemplateIds, allTemplates, templateMultipliers])

  if (!template) return null

  const hasItemChanges =
    removedIds.size > 0 || modifiedIds.size > 0 || addedItems.length > 0 || templateItems.length > 0
  const nameChanged = name !== template.name
  const hasAnyChange = hasItemChanges || nameChanged

  const visibleItems = localItems.filter((i) => !removedIds.has(i.id))

  const handleRemoveItem = (itemId: number) => {
    setRemovedIds(new Set([...removedIds, itemId]))
    if (editingId === itemId) setEditingId(null)
  }

  const handleResetItem = (itemId: number) => {
    const original = template.items.find((i) => i.id === itemId)
    if (!original) return
    setLocalItems((prev) => prev.map((i) => (i.id === itemId ? toLocalItem(original) : i)))
    const next = new Set(modifiedIds)
    next.delete(itemId)
    setModifiedIds(next)
  }

  const handleEditConfirm = (item: LocalItem) => {
    setLocalItems((prev) => prev.map((i) => (i.id === item.id ? item : i)))
    setModifiedIds(new Set([...modifiedIds, item.id]))
    setEditingId(null)
  }

  const handleAddItems = (items: MealItemInput[]) => {
    setAddedItems((prev) => [...prev, ...items])
  }

  const handleSave = async () => {
    await run(async () => {
      try {
        const updateItems: ItemUpdate[] = localItems
          .filter((i) => modifiedIds.has(i.id) && !removedIds.has(i.id))
          .map((i) => ({
            id: i.id,
            name: i.name,
            quantity: i.quantity || null,
            calories: i.calories,
            protein_g: i.protein_g,
            fat_g: i.fat_g,
            carb_g: i.carb_g,
            scale: i.scale,
          }))
        const allAddItems = [...addedItems, ...templateItems]
        await regenerateTemplate(template.id, {
          remove_item_ids: removedIds.size > 0 ? [...removedIds] : undefined,
          update_items: updateItems.length > 0 ? updateItems : undefined,
          add_items: allAddItems.length > 0 ? allAddItems : undefined,
          name: nameChanged ? name.trim() : undefined,
        })
        toast.success('テンプレートを更新しました')
        onSaved()
        onOpenChange(false)
      } catch {
        toast.error('更新に失敗しました')
      }
    })
  }

  const handleDelete = async () => {
    if (!(await confirmDialog({ title: 'このテンプレートを削除しますか？', destructive: true }))) return
    await run(async () => {
      try {
        await deleteTemplate(template.id)
        toast.success('削除しました')
        onSaved()
        onOpenChange(false)
      } catch {
        toast.error('削除に失敗しました')
      }
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>テンプレートを編集</DialogTitle>
          <DialogDescription className="text-xs">食品の編集・削除・追加ができます</DialogDescription>
        </DialogHeader>

        <div className="flex-1 space-y-3 overflow-y-auto overflow-x-hidden pr-1">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">テンプレート名</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="テンプレート名" />
          </div>

          {/* アイテムリスト */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">食品一覧</label>
            <div className="divide-y rounded-md border">
              {visibleItems.map((item) =>
                editingId === item.id ? (
                  <EditItemRow
                    key={item.id}
                    item={item}
                    onConfirm={handleEditConfirm}
                    onCancel={() => setEditingId(null)}
                  />
                ) : (
                  <div
                    key={item.id}
                    className={`flex items-center gap-1 px-2 py-1.5 ${modifiedIds.has(item.id) ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium">
                        {item.name}
                        {(() => {
                          const q = scaleQuantity(item.quantity, item.scale ?? 1)
                          return q && <span className="ml-1 text-muted-foreground">{q}</span>
                        })()}
                      </p>
                      <p className="text-[10px] text-muted-foreground">
                        {item.calories}kcal P{item.protein_g} F{item.fat_g} C{item.carb_g}
                      </p>
                    </div>
                    {modifiedIds.has(item.id) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-muted-foreground"
                        onClick={() => handleResetItem(item.id)}
                        title="元に戻す"
                      >
                        <RotateCcw className="size-3" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-muted-foreground"
                      onClick={() => setEditingId(item.id)}
                      title="編集"
                    >
                      <Pencil className="size-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-destructive"
                      onClick={() => handleRemoveItem(item.id)}
                      title="削除"
                    >
                      <X className="size-3" />
                    </Button>
                  </div>
                ),
              )}
              {/* 追加予定の新規アイテム */}
              {addedItems.map((item, idx) => (
                <div
                  key={`new-${idx}`}
                  className="flex items-center gap-1 bg-green-50 px-2 py-1.5 dark:bg-green-950/30"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium">
                      {item.name}
                      {(() => {
                        const q = scaleQuantity(item.quantity, item.scale ?? 1)
                        return q && <span className="ml-1 text-muted-foreground">{q}</span>
                      })()}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {item.calories}kcal P{item.protein_g} F{item.fat_g} C{item.carb_g}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0 text-destructive"
                    onClick={() => setAddedItems((prev) => prev.filter((_, i) => i !== idx))}
                    title="削除"
                  >
                    <X className="size-3" />
                  </Button>
                </div>
              ))}
            </div>
          </div>

          {/* 食品追加セクション */}
          <FoodAddPanel
            openModes={openModes}
            onToggleMode={(mode) =>
              setOpenModes((prev) => {
                const next = new Set(prev)
                if (next.has(mode)) next.delete(mode)
                else next.add(mode)
                return next
              })
            }
            onAdd={handleAddItems}
            excludeTemplateId={template.id}
            onTemplateChanged={fetchTemplates}
            selectedTemplateIds={selectedTemplateIds}
            onToggleTemplate={toggleTemplate}
            templateMultipliers={templateMultipliers}
            onTemplateMultiplierChange={(id, value) => {
              setTemplateMultipliers((prev) => {
                const next = new Map(prev)
                next.set(id, value)
                return next
              })
            }}
          />
        </div>

        <DialogFooter className="flex-row gap-2">
          <Button variant="destructive" onClick={handleDelete} disabled={busy} className="gap-1">
            <Trash2 className="size-3.5" />
            削除
          </Button>
          <Button onClick={handleSave} disabled={busy || !hasAnyChange || !name.trim()} className="flex-1 gap-1">
            {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
            保存
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ==============================================================================
// EditItemRow
// ==============================================================================

function EditItemRow({
  item,
  onConfirm,
  onCancel,
}: {
  item: LocalItem
  onConfirm: (item: LocalItem) => void
  onCancel: () => void
}) {
  const initialScale = item.scale > 0 ? item.scale : 1
  const baseRef = useRef({
    calories: item.calories / initialScale,
    protein_g: item.protein_g / initialScale,
    fat_g: item.fat_g / initialScale,
    carb_g: item.carb_g / initialScale,
  })
  const [form, setForm] = useState(item)
  const [multiplier, setMultiplier] = useState(initialScale.toString())

  const handleMultiplierChange = (raw: string) => {
    setMultiplier(raw)
    const mult = parseFloat(raw)
    if (!Number.isFinite(mult) || mult <= 0) return
    const base = baseRef.current
    setForm((prev) => ({
      ...prev,
      calories: Math.round(base.calories * mult),
      protein_g: Math.round(base.protein_g * mult * 10) / 10,
      fat_g: Math.round(base.fat_g * mult * 10) / 10,
      carb_g: Math.round(base.carb_g * mult * 10) / 10,
    }))
  }

  const handleConfirm = () => {
    const mult = parseFloat(multiplier)
    if (!Number.isFinite(mult) || mult <= 0) {
      // multiplier が不正な状態で確定すると、form.calories/P/F/C は
      // 前回の倍率で計算済みの値のまま scale=1 として保存され不整合になるため拒否する。
      toast.error('倍率には 0 より大きい数値を入力してください')
      return
    }
    onConfirm({ ...form, scale: mult })
  }

  return (
    <div className="space-y-2 bg-muted/50 p-2">
      <div className="grid grid-cols-2 gap-1.5">
        <Input
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="名前"
          className="h-7 text-xs"
        />
        <Input
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
          placeholder="量"
          className="h-7 text-xs"
        />
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        <div className="space-y-0.5">
          <label className="text-[10px] text-muted-foreground">kcal</label>
          <Input
            value={form.calories}
            onChange={(e) => setForm({ ...form, calories: parseFloat(e.target.value) || 0 })}
            className="h-7 text-xs"
            type="number"
          />
        </div>
        <div className="space-y-0.5">
          <label className="text-[10px] text-muted-foreground">P(たんぱく)</label>
          <Input
            value={form.protein_g}
            onChange={(e) => setForm({ ...form, protein_g: parseFloat(e.target.value) || 0 })}
            className="h-7 text-xs"
            type="number"
          />
        </div>
        <div className="space-y-0.5">
          <label className="text-[10px] text-muted-foreground">F(脂質)</label>
          <Input
            value={form.fat_g}
            onChange={(e) => setForm({ ...form, fat_g: parseFloat(e.target.value) || 0 })}
            className="h-7 text-xs"
            type="number"
          />
        </div>
        <div className="space-y-0.5">
          <label className="text-[10px] text-muted-foreground">C(糖質)</label>
          <Input
            value={form.carb_g}
            onChange={(e) => setForm({ ...form, carb_g: parseFloat(e.target.value) || 0 })}
            className="h-7 text-xs"
            type="number"
          />
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <label className="text-xs text-muted-foreground">倍率:</label>
        <Input
          value={multiplier}
          onChange={(e) => handleMultiplierChange(e.target.value)}
          className="h-7 w-16 text-xs"
          type="number"
          step="0.1"
          min="0.1"
        />
        <div className="ml-auto flex gap-1">
          <Button size="sm" className="h-6 px-2 text-xs" onClick={handleConfirm}>
            確定
          </Button>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={onCancel}>
            取消
          </Button>
        </div>
      </div>
    </div>
  )
}
